import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { WhatsAppChatSummary } from '../api/whatsapp';

type SupabaseChatRow = {
  jid: string;
  name: string;
  last_message_text: string | null;
  last_message_timestamp: string | null;
  unread_count: number | null;
  updated_at: string;
};

interface UseSupabaseChatsOptions {
  daysBack?: number;        // Time filter in days (null = no filter)
  specificJids?: string[];  // Fetch specific chats by JID
  enabled?: boolean;
}

async function fetchChatsFromSupabase(options: UseSupabaseChatsOptions = {}): Promise<WhatsAppChatSummary[]> {
  const { daysBack, specificJids } = options;

  let query = supabase
    .from('whatsapp_chats')
    .select('jid, name, last_message_text, last_message_timestamp, unread_count, updated_at');

  // Apply time filter if specified
  if (daysBack !== undefined && daysBack !== null) {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysBack);
    query = query.gte('last_message_timestamp', cutoffDate.toISOString());
  }

  // Apply JID filter if specified
  if (specificJids && specificJids.length > 0) {
    query = query.in('jid', specificJids);
  }

  query = query.order('updated_at', { ascending: false });

  const { data, error } = await query;

  if (error) {
    throw new Error(error.message);
  }

  const rows = (data as SupabaseChatRow[] | null) ?? [];

  return rows.map((row) => ({
    jid: row.jid,
    name: row.name,
    lastMessage: row.last_message_text && row.last_message_timestamp
      ? {
          id: '', // Not available in chat summary
          jid: row.jid,
          text: row.last_message_text,
          timestamp: new Date(row.last_message_timestamp).getTime(),
          direction: 'incoming' as const, // We don't store direction in summary
        }
      : undefined,
    unreadCount: row.unread_count ?? 0,
    updatedAt: new Date(row.updated_at).getTime(),
  }));
}

export function useSupabaseChats(options: UseSupabaseChatsOptions = {}) {
  const { enabled = true, ...queryOptions } = options;

  return useQuery<WhatsAppChatSummary[]>({
    queryKey: ['supabase-whatsapp-chats', queryOptions],
    queryFn: () => fetchChatsFromSupabase(queryOptions),
    enabled,
    staleTime: 1000 * 30, // 30 seconds
  });
}
